import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Trash2, X, Loader2, RefreshCw, BookOpen, Users, Search, Pencil } from 'lucide-react'
import { subjectsApi, classesApi, teachersApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'

const emptyForm = { name: '', classId: '' }
const getClassId = (item) => item?._id || item?.id || item
const getTeacherId = (item) => item?._id || item?.id || item
const getClassLabel = (item) => {
  if (!item) return 'No class assigned'
  if (typeof item === 'string') return item
  const name = item.name || item.className || 'Class'
  return item.section ? `${name} — ${item.section}` : name
}
const getTeacherName = (item) => item?.name || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || 'Teacher'
const getSubjectClass = (subject) => subject?.classId || subject?.class || null
const normalizeName = (value) => value.trim().replace(/\b\w/g, (c) => c.toUpperCase())

export default function SubjectsPage() {
  const [subjects, setSubjects] = useState([])
  const [classes, setClasses] = useState([])
  const [teachers, setTeachers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [filterClass, setFilterClass] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState(null)
  const [assignModal, setAssignModal] = useState(null)
  const [assignTeacherIds, setAssignTeacherIds] = useState([])
  const [assigning, setAssigning] = useState(false)
  const [teacherSearch, setTeacherSearch] = useState('')
  const [teacherActionKey, setTeacherActionKey] = useState('')
  const [form, setForm] = useState(emptyForm)
  const [deleteId, setDeleteId] = useState(null)

  const teacherLookup = useMemo(
    () => Object.fromEntries(teachers.map((teacher) => [getTeacherId(teacher), teacher])),
    [teachers]
  )

  const classLookup = useMemo(
    () => Object.fromEntries(classes.map((classItem) => [getClassId(classItem), classItem])),
    [classes]
  )

  const fetchData = async () => {
    try {
      setLoading(true)
      const [subjectsData, classesData, teachersData] = await Promise.allSettled([
        filterClass ? subjectsApi.getByClass(filterClass) : subjectsApi.getAll(),
        classesApi.getAll(),
        teachersApi.getAll(),
      ])
      if (subjectsData.status === 'fulfilled') {
        setSubjects(Array.isArray(subjectsData.value) ? subjectsData.value : subjectsData.value?.subjects ?? [])
      }
      if (classesData.status === 'fulfilled') {
        setClasses(Array.isArray(classesData.value) ? classesData.value : classesData.value?.classes ?? [])
      }
      if (teachersData.status === 'fulfilled') {
        setTeachers(Array.isArray(teachersData.value) ? teachersData.value : teachersData.value?.users ?? [])
      }
    } catch {
      toast.error('Failed to load subjects')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [filterClass])

  const openAdd = () => {
    setEditId(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (subject) => {
    setEditId(subject._id || subject.id)
    setForm({
      name: subject.name || '',
      classId: String(getClassId(getSubjectClass(subject)) || ''),
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Subject name is required'); return }
    const payload = { ...form, name: normalizeName(form.name) }
    try {
      setSaving(true)
      if (editId) {
        const updated = await subjectsApi.update(editId, payload)
        setSubjects((prev) => prev.map((s) => (s._id || s.id) === editId ? { ...s, ...updated } : s))
        toast.success('Subject updated')
      } else {
        const created = await subjectsApi.create(payload)
        setSubjects((prev) => [...prev, created])
        toast.success('Subject created')
      }
      setModalOpen(false)
      setForm(emptyForm)
      setEditId(null)
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${editId ? 'update' : 'create'} subject`)
    } finally {
      setSaving(false)
    }
  }

  const openAssign = (subject) => {
    setAssignModal(subject)
    const existing = subject.teachers?.map((t) => t._id || t.id || t) ?? []
    setAssignTeacherIds(existing)
    setTeacherSearch('')
  }

  const syncSubjectTeachers = async (subject, teacherIds, successMessage) => {
    try {
      setAssigning(true)
      const updated = await subjectsApi.assignTeachers(subject._id || subject.id, { teacherIds })
      setSubjects((prev) => prev.map((s) => (s._id || s.id) === (subject._id || subject.id) ? { ...s, ...updated } : s))
      if (assignModal && (assignModal._id || assignModal.id) === (subject._id || subject.id)) {
        setAssignModal((prev) => prev ? { ...prev, ...updated } : prev)
        setAssignTeacherIds(teacherIds)
      }
      toast.success(successMessage)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update teachers')
    } finally {
      setAssigning(false)
    }
  }

  const handleAssign = async () => {
    if (!assignModal) return
    await syncSubjectTeachers(assignModal, assignTeacherIds, 'Teachers updated')
    setAssignModal(null)
  }

  const toggleTeacher = (id) => {
    setAssignTeacherIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    )
  }

  const handleRemoveTeacher = async (subject, teacherId) => {
    const existing = (subject.teachers || []).map((teacher) => getTeacherId(teacher)).filter(Boolean)
    const nextTeacherIds = existing.filter((id) => String(id) !== String(teacherId))
    setTeacherActionKey(`${subject._id || subject.id}:${teacherId}`)
    try {
      await syncSubjectTeachers(subject, nextTeacherIds, 'Teacher unassigned from subject')
    } finally {
      setTeacherActionKey('')
    }
  }

  const filteredTeachers = useMemo(() => {
    const query = teacherSearch.trim().toLowerCase()
    const list = teachers.filter((teacher) => {
      if (!query) return true
      return [getTeacherName(teacher), teacher.email, teacher.mobile]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    })

    return list.sort((a, b) => {
      const aSelected = assignTeacherIds.includes(getTeacherId(a))
      const bSelected = assignTeacherIds.includes(getTeacherId(b))
      if (aSelected !== bSelected) return aSelected ? -1 : 1
      return getTeacherName(a).localeCompare(getTeacherName(b))
    })
  }, [assignTeacherIds, teacherSearch, teachers])

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Subjects</h2>
          <p className="text-sm text-gray-500 mt-0.5">{loading ? '...' : `${subjects.length} subjects`}</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button onClick={fetchData} className="btn-secondary p-2"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={openAdd} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Subject
          </button>
        </div>
      </div>

      {/* Class filter chips */}
      <div className="card p-4 flex gap-2 items-center flex-wrap">
        <span className="text-sm font-medium text-gray-600 mr-1">Filter:</span>
        <button
          onClick={() => setFilterClass('')}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full border-[1.5px] transition-colors ${!filterClass ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
        >
          All
        </button>
        {classes.map((c) => (
          <button
            key={getClassId(c)}
            onClick={() => setFilterClass(getClassId(c))}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border-[1.5px] transition-colors ${filterClass === getClassId(c) ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            {getClassLabel(c)}
          </button>
        ))}
      </div>

      {/* Subject list */}
      <div className="space-y-2">
        {loading ? [...Array(6)].map((_, i) => (
          <div key={i} className="card flex items-center gap-4 p-4 animate-pulse">
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-100 rounded w-1/3" />
              <div className="h-3 bg-gray-100 rounded w-1/4" />
            </div>
          </div>
        )) : subjects.length === 0 ? (
          <div className="card py-12 text-center">
            <BookOpen className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No subjects found</p>
          </div>
        ) : subjects.map((s) => {
          const subjectClass = classLookup[getClassId(getSubjectClass(s))] || getSubjectClass(s)
          const subjectClassLabel = getClassLabel(subjectClass)
          const assignedTeachers = (s.teachers || [])
            .map((teacher) => (typeof teacher === 'object' ? teacher : teacherLookup[getTeacherId(teacher)]))
            .filter(Boolean)

          return (
            <div key={s._id || s.id} className="card flex items-center gap-4 p-4">
              {/* Book icon */}
              <div className="w-[42px] h-[42px] bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>

              {/* Name + class */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-900 truncate">{s.name}</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{subjectClassLabel}</p>
              </div>

              {/* Teacher count + assign */}
              <div className="flex items-center gap-1.5 text-xs text-gray-400 flex-shrink-0">
                <Users className="w-3.5 h-3.5" />
                <button
                  onClick={() => openAssign(s)}
                  className="hover:text-primary-600 transition-colors font-medium"
                  title="Manage teachers"
                >
                  {assignedTeachers.length} teacher{assignedTeachers.length !== 1 ? 's' : ''}
                </button>
              </div>

              {/* Edit button */}
              <button
                onClick={() => openEdit(s)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors flex-shrink-0"
                title="Edit subject"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>

              {/* Delete button */}
              <button
                onClick={() => setDeleteId(s._id || s.id)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                title="Delete subject"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )
        })}
      </div>

      {/* Add / Edit Subject Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[92vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="font-bold text-gray-900">{editId ? 'Edit Subject' : 'Add Subject'}</h3>
              <button onClick={() => { setModalOpen(false); setEditId(null) }}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              {/* Section 1: Subject Name */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Subject Name</p>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject Name *</label>
                  <input
                    className="input-field w-full"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Mathematics"
                    autoFocus
                  />
                </div>
              </div>

              {/* Section 2: Select Class */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">Select Class *</p>
                {classes.length === 0 ? (
                  <p className="text-xs text-gray-400">No classes available</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {classes.map((c) => {
                      const cid = getClassId(c)
                      const selected = form.classId === String(cid)
                      return (
                        <button
                          key={cid}
                          type="button"
                          onClick={() => setForm({ ...form, classId: selected ? '' : String(cid) })}
                          className={`text-xs font-semibold px-3.5 py-2 rounded-full border-[1.5px] transition-colors ${
                            selected
                              ? 'bg-primary-600 text-white border-primary-600'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {getClassLabel(c)}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="px-5 pb-5 pt-2 flex gap-3 justify-end flex-shrink-0 border-t border-gray-100">
              <button onClick={() => { setModalOpen(false); setEditId(null) }} className="btn-secondary">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editId ? 'Save Changes' : 'Create Subject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Teachers Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-900">Manage Subject Teachers</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {assignModal.name} · {getClassLabel(classLookup[getClassId(getSubjectClass(assignModal))] || getSubjectClass(assignModal))}
                </p>
              </div>
              <button onClick={() => setAssignModal(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            <div className="p-6 space-y-5">
              <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">Selected Teachers</p>
                <div className="flex flex-wrap gap-2 mt-3 min-h-7">
                  {assignTeacherIds.length > 0 ? assignTeacherIds.map((teacherId) => {
                    const teacher = teacherLookup[teacherId]
                    if (!teacher) return null
                    return (
                      <div key={teacherId} className="inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-white px-2.5 py-1 text-xs font-medium text-blue-700">
                        <Avatar src={teacher.avatar} name={getTeacherName(teacher)} size="xs" />
                        <span>{getTeacherName(teacher)}</span>
                        <button
                          type="button"
                          onClick={() => toggleTeacher(teacherId)}
                          className="rounded-full p-0.5 text-blue-500 transition-colors hover:bg-blue-100 hover:text-red-500"
                          title="Remove from selection"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )
                  }) : (
                    <span className="text-xs text-blue-700">No teachers selected yet. Pick one or more below.</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  value={teacherSearch}
                  onChange={(e) => setTeacherSearch(e.target.value)}
                  placeholder="Search teacher by name, email, or mobile..."
                  className="bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none w-full"
                />
              </div>

              <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                {filteredTeachers.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">No teachers found</p>
                ) : filteredTeachers.map((t) => {
                  const tid = t._id || t.id
                  const checked = assignTeacherIds.includes(tid)
                  return (
                    <label key={tid} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors cursor-pointer ${checked ? 'border-primary-200 bg-primary-50' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleTeacher(tid)} className="w-4 h-4 accent-blue-600" />
                      <Avatar src={t.avatar} name={getTeacherName(t)} size="sm" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{t.name || `${t.firstName || ''} ${t.lastName || ''}`.trim() || '—'}</p>
                        <p className="text-xs text-gray-400">{t.email || t.mobile || 'No contact info'}</p>
                      </div>
                      {checked && <span className="ml-auto rounded-full bg-primary-600 px-2.5 py-1 text-[11px] font-semibold text-white">Selected</span>}
                    </label>
                  )
                })}
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button onClick={() => setAssignModal(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleAssign} disabled={assigning} className="btn-primary flex items-center gap-2">
                {assigning && <Loader2 className="w-4 h-4 animate-spin" />} Save Teachers
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Delete Subject?</h3>
            <p className="text-sm text-gray-500 mb-5">This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="btn-secondary flex-1">Cancel</button>
              <button
                onClick={async () => {
                  try {
                    await subjectsApi.remove(deleteId)
                    setSubjects((p) => p.filter((s) => (s._id || s.id) !== deleteId))
                    toast.success('Subject deleted')
                  } catch {
                    toast.error('Failed')
                  } finally {
                    setDeleteId(null)
                  }
                }}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
